import { useMutation, useQueryClient } from '@tanstack/react-query';

import { isDefined, isEmpty, isNotDefined, isOfType } from '@hsp/utilities';

import { useAppSelector } from 'hooks/store';
import { useSaveStoreSelector } from 'pages/audience/shared/save/hooks';
import { sharedABQueryKeys } from 'pages/audience/shared/utilities';
import { audienceService } from 'services';
import { selectUserId } from 'store/selectors';
import { AudienceStatus, IAudienceShop, MultiRfmSearchApi } from 'types/api/audience';
import { SaveForm } from 'types/pages/audience-builder/shared/save';

export const useSaveMasterFile = () => {
  const userId = useAppSelector(selectUserId);

  const queryClient = useQueryClient();
  const data = useSaveStoreSelector(({ data }) => data);

  return useMutation({
    mutationFn: async (formData: SaveForm) => {
      const { type } = formData;

      if (isNotDefined(userId)) {
        return Promise.reject();
      }

      if (type !== 'master-file' || !isOfType<IAudienceShop>(data, 'categoryId')) {
        return Promise.reject();
      }

      const { client, description, giftDateEnd, giftDateStart, ...rest } = formData;
      const shop: IAudienceShop = {
        ...data,
        ...rest,
        clientId: client?.id ?? null,
        clientName: client?.name ?? null,
        description: isEmpty(description) ? null : description,
        giftDateEnd: giftDateEnd?.toISOString(),
        giftDateStart: giftDateStart?.toISOString(),
        quantity: 0,
        statusId: AudienceStatus.Active,
        userId,
      };

      const { parameters } = shop;

      if (isOfType<MultiRfmSearchApi>(parameters, 'rfmSets')) {
        return await audienceService.saveMasterFileMultiRfm(shop);
      }

      return await audienceService.saveMasterFile(shop);
    },
    onSuccess: (result, formData) => {
      if (result?.success) {
        if (isOfType<IAudienceShop>(formData, 'categoryId') && isDefined(formData.shopId)) {
          queryClient.invalidateQueries({ queryKey: sharedABQueryKeys.shop.default(formData.shopId) });
        }
      }
    },
  });
};
